// src/components/common/vibe/EditPost.js
import React, { useState } from 'react';
import { X, Image as ImageIcon, Smile, Award } from 'lucide-react';
import vibeService from '../../../services/vibe.service';
import { MOODS } from '../../../constants/vibeConstants';
import { useEmployees } from '../../../contexts/EmployeeContext';

const EditPost = ({ isOpen, onClose, post, onPostUpdated, theme }) => {
    const { employees } = useEmployees();
    const [loading, setLoading] = useState(false);
    const isKudosPost = post?.post_category === 'kudos';

    const [formData, setFormData] = useState({
        content: post?.content || '',
        mood: post?.mood || null,
        visibility: post?.visibility || 'company',
        mentioned_employees: post?.mentioned_employees || []
    });

    const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
    const [employeeSearch, setEmployeeSearch] = useState('');

    // Filter employees for selection
    const filteredEmployees = employees.filter(emp =>
        `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(employeeSearch.toLowerCase()) &&
        !formData.mentioned_employees.includes(emp.id)
    );

    const handleAddEmployee = (employee) => {
        setFormData({
            ...formData,
            mentioned_employees: [...formData.mentioned_employees, employee.id]
        });
        setEmployeeSearch('');
        setShowEmployeeDropdown(false);
    };

    const handleRemoveEmployee = (employeeId) => {
        setFormData({
            ...formData,
            mentioned_employees: formData.mentioned_employees.filter(id => id !== employeeId)
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        //console.log('✏️ Updating post...', { postId: post.id, isKudos: isKudosPost });

        // Validation
        if (!formData.content.trim()) {
            alert('Post content cannot be empty.');
            return;
        }

        if (isKudosPost && formData.mentioned_employees.length === 0) {
            alert('Kudos must mention at least one employee.');
            return;
        }

        setLoading(true);
        try {
            const updateData = {
                content: formData.content.trim(),
                mood: formData.mood,
                visibility: formData.visibility
            };

            // For kudos posts, include mentioned_employees
            if (isKudosPost) {
                updateData.mentioned_employees = formData.mentioned_employees;
            }

            await vibeService.updatePost(post.id, updateData);

            //console.log('✅ Post updated successfully');
            alert('✅ Post updated successfully!');

            if (onPostUpdated) {
                await onPostUpdated();
            }

            onClose();
        } catch (error) {
            console.error('❌ Error updating post:', error);
            alert(`❌ Failed to update post: ${error.response?.data?.message || error.message || 'Unknown error'}`);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className={`w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl p-6 m-4 ${
                theme === 'dark' ? 'bg-gray-800' : 'bg-white'
            }`}>
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <h2 className={`text-2xl font-bold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                        ✏️ Edit Post
                    </h2>
                    <button
                        onClick={onClose}
                        className={`p-2 rounded-lg ${
                            theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                        }`}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Content */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            Post Content <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            value={formData.content}
                            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                            placeholder="What's on your mind?"
                            rows={6}
                            maxLength={5000}
                            className={`w-full px-4 py-3 rounded-lg border resize-none ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                            }`}
                        />
                        <div className={`text-xs text-right mt-1 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                            {formData.content.length}/5000 characters
                        </div>
                    </div>

                    {/* Employee Selection for Kudos */}
                    {isKudosPost && (
                        <div>
                            <label className={`block text-sm font-medium mb-2 ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                                <Award className="w-4 h-4 inline mr-1" />
                                Tagged Employees <span className="text-red-500">*</span>
                            </label>

                            {/* Selected Employees */}
                            {formData.mentioned_employees.length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-2">
                                    {formData.mentioned_employees.map(empId => {
                                        const emp = employees.find(e => e.id === empId);
                                        if (!emp) return null;
                                        return (
                                            <span
                                                key={empId}
                                                className={`px-3 py-1 rounded-full text-sm flex items-center gap-2 ${
                                                    theme === 'dark'
                                                        ? 'bg-yellow-900/30 text-yellow-400'
                                                        : 'bg-yellow-100 text-yellow-700'
                                                }`}
                                            >
                                                {emp.first_name} {emp.last_name}
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveEmployee(empId)}
                                                    className="hover:bg-black/10 dark:hover:bg-white/10 rounded-full"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </span>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Search Input */}
                            <div className="relative">
                                <input
                                    type="text"
                                    value={employeeSearch}
                                    onChange={(e) => {
                                        setEmployeeSearch(e.target.value);
                                        setShowEmployeeDropdown(true);
                                    }}
                                    onFocus={() => setShowEmployeeDropdown(true)}
                                    placeholder="Search and select employees..."
                                    className={`w-full px-4 py-2 rounded-lg border ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                    }`}
                                />

                                {/* Employee Dropdown */}
                                {showEmployeeDropdown && employeeSearch && filteredEmployees.length > 0 && (
                                    <div className={`absolute z-50 mt-1 w-full max-h-48 overflow-y-auto rounded-lg border shadow-lg ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600'
                                            : 'bg-white border-gray-200'
                                    }`}>
                                        {filteredEmployees.slice(0, 10).map((emp) => (
                                            <button
                                                key={emp.id}
                                                type="button"
                                                onClick={() => handleAddEmployee(emp)}
                                                className={`w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center gap-2 ${
                                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                }`}
                                            >
                                                <div className="w-8 h-8 bg-yellow-600 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                                    {emp.first_name[0]}{emp.last_name[0]}
                                                </div>
                                                <div>
                                                    <div className="font-medium">{emp.first_name} {emp.last_name}</div>
                                                    <div className="text-xs opacity-75">{emp.designation}</div>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Mood Selection */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            <Smile className="w-4 h-4 inline mr-1" />
                            How are you feeling?
                        </label>
                        <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                            {MOODS.map((mood) => (
                                <button
                                    key={mood.value}
                                    type="button"
                                    onClick={() => setFormData({ ...formData, mood: formData.mood === mood.value ? null : mood.value })}
                                    className={`p-3 rounded-lg border-2 text-center transition-all ${
                                        formData.mood === mood.value
                                            ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                                            : theme === 'dark'
                                                ? 'border-gray-700 hover:border-gray-600'
                                                : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    <div className="text-2xl mb-1">{mood.emoji}</div>
                                    <div className={`text-xs font-medium ${
                                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                                    }`}>
                                        {mood.label}
                                    </div>
                                </button>
                            ))}
                        </div>
                        {formData.mood && (
                            <button
                                type="button"
                                onClick={() => setFormData({ ...formData, mood: null })}
                                className={`mt-2 text-sm ${
                                    theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                                } hover:underline`}
                            >
                                Clear mood
                            </button>
                        )}
                    </div>

                    {/* Visibility */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            Visibility
                        </label>
                        <select
                            value={formData.visibility}
                            onChange={(e) => setFormData({ ...formData, visibility: e.target.value })}
                            className={`w-full px-4 py-2 rounded-lg border ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                        >
                            <option value="company">Company-wide</option>
                            <option value="department">Department only</option>
                            <option value="team">Team only</option>
                        </select>
                    </div>

                    {/* Submit Buttons */}
                    <div className="flex gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className={`flex-1 px-6 py-3 rounded-lg font-medium ${
                                theme === 'dark'
                                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className={`flex-1 px-6 py-3 rounded-lg font-medium text-white ${
                                loading
                                    ? 'bg-blue-400 cursor-not-allowed'
                                    : 'bg-blue-600 hover:bg-blue-700'
                            }`}
                        >
                            {loading ? 'Updating...' : 'Update Post'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditPost;
